import DOMPurify from 'isomorphic-dompurify'
import { useEffect, useState } from 'react'
import { isMobilePhone } from 'validator'

import {
  EChannel,
  TGetOtpResult,
  TOtpParams,
  TSetSessionParams,
} from '../actions/auth/auth-actions.type'
import getOtp from '../actions/auth/get-otp'
import postOtp from '../actions/auth/post-otp'
import setSession from '../actions/auth/set-session'
import isEmailValidator from '../helpers/is-email-validator'
import {
  resetOtpPhone,
  setOtpUsernameAttempt,
  setOtpUsernameChannel,
  setOtpTokenVerifier,
  setRateLimitReached,
  setRateLimitLockedTime,
  setOtpUsername,
  setOtpMaskedPhoneNumber,
  setLastResendAttemptTime,
  setSubmitOtpError,
  setIsInputRateLimitReached,
} from '../store/slice/phone-otp-slice'
import { EApiErrorCode, TApiResult } from '../types/global.types'

import { useAppDispatch } from './use-app-dispatch'
import { useAppSelector } from './use-app-selector'
import useLoaderTransition from './use-loader-transition'
import useToast from './use-toast'

const THIRTY_SEC = 30 * 1000

const usePhoneVerify = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()

  const { startLoaderTransition } = useLoaderTransition()

  const [isFinishPostOtp, setIsFinishPostOtp] = useState(false)
  const { channel, tokenVerifier, username, rateLimitLockedTime } = useAppSelector(
    (state) => state.phoneOtp,
  )

  const { partnerId, authPartnerUrl } = useAppSelector((state) => state.user)

  const [otp, setOtp] = useState('')

  const onRateLimitExceeded = (
    // temporary unused after adjustment from BE
    response: Partial<TApiResult<TGetOtpResult>>,
    phoneNumber: string,
    callback?: () => void,
  ) => {
    dispatch(setOtpUsername(phoneNumber))
    dispatch(
      setRateLimitLockedTime(Date.now() + (response?.result?.remainingSeconds as number) * 1000),
    )

    dispatch(
      setRateLimitReached({
        isRateLimitReached: true,
      }),
    )

    if (callback) callback()
  }

  const onGetOtp = async ({
    username,
    onSuccess,
    onRateLimitExceeded: rateLimitcallback,
    noCredentials = true,
  }: {
    username: string
    onSuccess: () => void
    onRateLimitExceeded: () => void
    noCredentials?: boolean
  }) => {
    const isUsernameEmail = isEmailValidator(username)

    const response = await getOtp({
      phone_number: isUsernameEmail ? undefined : username,
      email: isUsernameEmail ? username : undefined,
      channel: EChannel.WHATSAPP,
      noCredentials,
    })

    if (response?.success) {
      const {
        result: { token_verifier, attempt, masked_phone_number },
      } = response as TApiResult<TGetOtpResult>

      dispatch(setOtpTokenVerifier(token_verifier))
      dispatch(setOtpUsernameAttempt(attempt))
      dispatch(setOtpUsername(username))
      dispatch(setLastResendAttemptTime(Date.now() + THIRTY_SEC))
      dispatch(setRateLimitLockedTime(0))

      dispatch(
        setRateLimitReached({
          isRateLimitReached: false,
        }),
      )

      dispatch(setOtpMaskedPhoneNumber(masked_phone_number))

      onSuccess && onSuccess()
    } else if (response?.error?.code === EApiErrorCode.OTP_REQUEST_LIMIT_EXCEEDED) {
      toast.show({
        title: 'OTP Request Rate Limit Exceeded',
        description: response?.error?.message || 'Oops something went wrong.',
        traceId: response?.traceId || '',
        type: 'error',
        transformTitle: false,
      })

      onRateLimitExceeded(response, username, rateLimitcallback)
    } else {
      toast.show({
        title: response?.error?.code ?? 'Error Occured',
        description: response?.error?.message,
        type: 'error',
        traceId: response?.traceId,
      })
    }
  }

  const onError = (response: Partial<TApiResult<TGetOtpResult>>) => {
    const errorCode = response?.error?.code as EApiErrorCode

    switch (errorCode) {
      case EApiErrorCode.OTP_INPUT_LIMIT_EXCEEDED:
        if (rateLimitLockedTime === 0) {
          dispatch(
            setRateLimitLockedTime(
              Date.now() + (response?.result?.remainingSeconds as number) * 1000,
            ),
          )
        }

        dispatch(setIsInputRateLimitReached(true))

        toast.show({
          title: 'OTP Input Rate Limit Exceeded',
          description: response?.error?.message,
          type: 'error',
          traceId: response?.traceId,
          autoCloseDuration: 3000,
          transformTitle: false,
        })
        break
      case EApiErrorCode.RATE_LIMIT_EXCEEDED:
      case EApiErrorCode.OTP_REQUEST_LIMIT_EXCEEDED:
        toast.show({
          title: 'OTP Request Rate Limit Exceeded',
          description: response?.error?.message,
          type: 'error',
          traceId: response?.traceId,
          autoCloseDuration: 3000,
          transformTitle: false,
        })

        if (rateLimitLockedTime === 0) {
          dispatch(
            setRateLimitLockedTime(
              Date.now() + (response?.result?.remainingSeconds as number) * 1000,
            ),
          )
        }

        dispatch(
          setRateLimitReached({
            isRateLimitReached: true,
          }),
        )
        break
      default:
        toast.show({
          title: response?.error?.code ?? 'Error Occured',
          description: response?.error?.message,
          type: 'error',
          traceId: response?.traceId,
        })
    }
  }

  const submitOtp = async (
    callback: (
      username: string,
      response?: TApiResult<{ access_token: string; refresh_token: string; survey_id?: string }>,
    ) => void,
    withCredential: boolean = true,
    session?: Partial<TSetSessionParams>,
  ) => {
    dispatch(setSubmitOtpError(''))

    const params: TOtpParams & { otp_code: string } = {
      otp_code: otp,
      channel,
    }

    if (isMobilePhone(username, 'id-ID')) {
      params.phone_number = username
    } else {
      params.email = username
    }

    const response = await postOtp(params, tokenVerifier, withCredential)

    setIsFinishPostOtp(true)

    if (response?.success) {
      const access_token = response?.result?.access_token
      const refresh_token = response?.result?.refresh_token

      await setSession({
        accessToken: access_token as string,
        refreshToken: refresh_token as string,
        username: (params?.phone_number || params?.email) as string,
        isGuest: !!partnerId as unknown as string,
        authPartnerUrl: authPartnerUrl || undefined,
        ...session,
      })

      dispatch(resetOtpPhone())
      return (
        callback &&
        callback(
          (params?.phone_number || params?.email) as string,
          response as TApiResult<{
            access_token: string
            refresh_token: string
            survey_id?: string
          }>,
        )
      )
    } else {
      onError(response as Partial<TApiResult<TGetOtpResult>>)
    }
  }

  const onOtpChange = (value: string) => setOtp(value)

  const onResendOtp = async (type: EChannel, noCredentials?: boolean) => {
    const params: TOtpParams = {
      channel: type,
      phone_number: isMobilePhone(username, 'id-ID') ? DOMPurify.sanitize(username) : undefined,
      email: isMobilePhone(username, 'id-ID') ? undefined : DOMPurify.sanitize(username),
      noCredentials,
    }

    const response = await getOtp(params)

    if (response?.success) {
      const {
        result: { token_verifier, attempt },
      } = response as TApiResult<TGetOtpResult>

      dispatch(setOtpTokenVerifier(token_verifier))
      dispatch(setOtpUsernameAttempt(attempt))
      dispatch(setOtpUsernameChannel(type))
      dispatch(setLastResendAttemptTime(Date.now() + attempt * THIRTY_SEC))

      toast.show({
        title: 'Resend Code Success',
        description: 'A new verification code has been successfully sent to your phone number.',
        type: 'success',
      })
    } else {
      onError(response)
    }
  }

  useEffect(() => {
    if (otp.length === 6) {
      dispatch(setSubmitOtpError(''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp.length])

  useEffect(() => {
    return () => {
      dispatch(setSubmitOtpError(''))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = async (
    callback: (
      username: string,
      response?: TApiResult<{ access_token: string; refresh_token: string; survey_id?: string }>,
    ) => void,
    withCredential: boolean = true,
    session?: Partial<TSetSessionParams>,
  ) => {
    if (otp.length === 6 && username) {
      startLoaderTransition(() => submitOtp(callback, withCredential, session), {
        title: 'Verifying your phone number',
        description: 'Please wait a moment.',
      })
    }

    if (otp.length < 6) {
      dispatch(setSubmitOtpError('Please input the 6 digit of the OTP code.'))
    }
  }

  return {
    onGetOtp,
    onResendOtp,
    onOtpChange,
    onSubmit,
    startLoaderTransition,
    isFinishPostOtp,
  }
}

export default usePhoneVerify
